import axios from 'axios'
import {
  DCUploadResponse,
  DCParseResponse,
  DCUploadDetails,
  DCItemDiscount,
  DCPriceComparison,
  DCParserConfig,
  Distributor,
  MasterMedicine,
  DCStats,
  PaginatedResponse,
  UploadAndProcessResponse,
  DCEligibleItem,
  DCManagedItem,
  DCOrder,
  DCOrderDetail,
  DCDemandMap,
  DCPurchase,
  DCPurchaseDetail,
  DCPurchaseItem,
  DCPurchaseUploadResponse,
  DCInventoryItem,
  DCInventorySummary,
  DCStoreTransfer,
  DCTransferDetail,
  DCAutoAllocateResult,
  DCStoreSplitReport
} from '../types/dc.types'

const API_BASE = '/api/dc'

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth header
api.interceptors.request.use((config) => {
  const email = localStorage.getItem('userEmail')
  if (email) {
    config.headers['x-user-email'] = email
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred'
    return Promise.reject(new Error(message))
  }
)

export const dcApi = {
  // One-click Upload & Process
  async uploadAndProcess(file: File, defaultDiscountPct: number = 8, discountOfferMode: string = 'combine'): Promise<UploadAndProcessResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('defaultDiscountPct', String(defaultDiscountPct))
    formData.append('discountOfferMode', discountOfferMode)

    const response = await api.post<{ success: boolean; data: UploadAndProcessResponse }>(
      '/upload-and-process',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      }
    )
    return response.data.data
  },

  async continueProcessing(params: {
    uploadId: number
    distributorId: number
    distributorName: string
    defaultDiscountPct?: number
    discountOfferMode?: string
  }): Promise<UploadAndProcessResponse> {
    const response = await api.post<{ success: boolean; data: UploadAndProcessResponse }>(
      '/continue-processing',
      params,
      { timeout: 120000 }
    )
    return response.data.data
  },

  // Upload Management
  async uploadFile(file: File): Promise<DCUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<{ success: boolean; data: DCUploadResponse }>('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data.data
  },

  async getUploads(params?: { page?: number; limit?: number; status?: string; distributorId?: number }) {
    const response = await api.get('/uploads', { params })
    return response.data.data
  },

  async getUploadDetails(id: number): Promise<DCUploadDetails> {
    const response = await api.get(`/uploads/${id}`)
    return response.data.data
  },

  async deleteUpload(id: number): Promise<void> {
    await api.delete(`/uploads/${id}`)
  },

  // Parsing & Processing
  async parseFile(params: { uploadId: number; filePath: string }): Promise<DCParseResponse> {
    const response = await api.post('/parse', params)
    return response.data.data
  },

  async applyColumnMapping(params: {
    uploadId: number
    filePath: string
    columnMappings: Record<number, string>
    skipRows?: number
    saveConfig?: boolean
    distributorName?: string
  }): Promise<{
    uploadId: number
    items: DCItemDiscount[]
    totalItems: number
    parserType: string
    mappingApplied: boolean
  }> {
    const response = await api.post('/apply-column-mapping', params)
    return response.data.data
  },

  async parseWithAI(params: {
    uploadId: number
    filePath?: string
    distributorName?: string
  }): Promise<{
    uploadId: number
    items: DCItemDiscount[]
    totalItems: number
    parserType: string
    aiParsed: boolean
    learnedRules?: {
      confidence: number
      notes: string
      savedConfig: boolean
    }
  }> {
    const response = await api.post('/parse-ai', params)
    return response.data.data
  },

  async confirmDistributor(params: {
    uploadId: number
    distributorId?: number
    distributorName: string
    defaultDiscountPct?: number
    offerDate?: string
  }): Promise<void> {
    await api.post('/confirm-distributor', params)
  },

  async matchItems(params: { uploadId: number; items: DCItemDiscount[] }): Promise<{
    items: DCItemDiscount[]
    matchStats: { total: number; matched: number; unmatched: number }
  }> {
    const response = await api.post('/match-items', params)
    return response.data.data
  },

  async manualMatch(params: { itemDiscountId: number; medicineId: number }): Promise<{
    medicineId: number
    medicineName: string
  }> {
    const response = await api.post('/manual-match', params)
    return response.data.data
  },

  async saveItems(params: {
    uploadId: number
    items: DCItemDiscount[]
    deleteExisting?: boolean
  }): Promise<{ success: boolean; itemCount: number }> {
    const response = await api.post('/save', params)
    return response.data.data
  },

  // Price Comparison
  async getMedicineComparison(medicineId: number): Promise<{
    medicine: MasterMedicine
    offers: DCItemDiscount[]
    bestOffer: DCItemDiscount | null
    offerCount: number
  }> {
    const response = await api.get(`/compare/${medicineId}`)
    return response.data.data
  },

  async getComparisons(params?: {
    page?: number
    limit?: number
    category?: string
    manufacturer?: string
    minDiscount?: number
    maxDiscount?: number
    search?: string
  }): Promise<PaginatedResponse<DCPriceComparison>> {
    const response = await api.get('/compare', { params })
    return response.data.data
  },

  async getBestPrices(params?: { page?: number; limit?: number }): Promise<DCPriceComparison[]> {
    const response = await api.get('/best-prices', { params })
    return response.data.data
  },

  async searchMedicines(query: string, limit?: number): Promise<MasterMedicine[]> {
    const response = await api.get('/search-medicines', { params: { query, limit } })
    return response.data.data
  },

  // PTR & Landing Cost
  async lookupPtr(medicineId: number, distributorId?: number): Promise<{
    ptr: number | null
    source: string | null
    confidence: number
  }> {
    const response = await api.post('/lookup-ptr', { medicineId, distributorId })
    return response.data.data
  },

  async calculateLandingCost(params: {
    ptr: number
    discountPct?: number
    buyQty?: number
    freeQty?: number
    gstPct?: number
  }): Promise<{
    landingCost: number
    effectiveDiscount: number
    discountedPtr: number
  }> {
    const response = await api.post('/calculate-landing', params)
    return response.data.data
  },

  // Parser Configurations
  async getParserConfigs(params?: { active?: boolean }): Promise<DCParserConfig[]> {
    const response = await api.get('/parser-configs', { params })
    return response.data.data
  },

  async getParserConfig(id: number): Promise<DCParserConfig> {
    const response = await api.get(`/parser-configs/${id}`)
    return response.data.data
  },

  async createParserConfig(config: Partial<DCParserConfig>): Promise<DCParserConfig> {
    const response = await api.post('/parser-configs', config)
    return response.data.data
  },

  async updateParserConfig(id: number, config: Partial<DCParserConfig>): Promise<void> {
    await api.put(`/parser-configs/${id}`, config)
  },

  async deleteParserConfig(id: number): Promise<void> {
    await api.delete(`/parser-configs/${id}`)
  },

  // Distribution Centers
  async getDistributionCenters(): Promise<Array<{
    id: number
    code: string
    name: string
    city: string | null
    state: string | null
    is_active: boolean
  }>> {
    const response = await api.get('/distribution-centers')
    return response.data.data
  },

  // Distributors
  async getDistributors(): Promise<Distributor[]> {
    const response = await api.get('/distributors')
    return response.data.data
  },

  async searchDistributors(query: string): Promise<Distributor[]> {
    const response = await api.get('/distributors/search', { params: { query } })
    return response.data.data
  },

  // Statistics
  async getStats(): Promise<DCStats> {
    const response = await api.get('/stats')
    return response.data.data
  },

  // Export
  async exportToExcel(params?: { distributorIds?: number[]; medicineIds?: number[] }): Promise<Blob> {
    const response = await api.get('/export/excel', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  // ============================================
  // DC Ordering - Eligible Items
  // ============================================

  async getEligibleItems(params?: { page?: number; limit?: number; search?: string }): Promise<{
    items: DCEligibleItem[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const response = await api.get('/ordering/eligible-items', { params })
    return response.data.data
  },

  async getEligibleItemDetail(medicineId: number): Promise<{
    medicine: MasterMedicine
    storeMonthBreakdown: Array<{
      store_id: number
      store_name: string
      sale_month: string
      distinct_bills: number
      total_qty: number
    }>
    priceComparison: {
      medicine: MasterMedicine
      offers: DCItemDiscount[]
      bestOffer: DCItemDiscount | null
      offerCount: number
    }
  }> {
    const response = await api.get(`/ordering/eligible-items/${medicineId}/detail`)
    return response.data.data
  },

  // ============================================
  // DC Ordering - Managed Items
  // ============================================

  async getManagedItems(params?: { page?: number; limit?: number; search?: string; status?: string }): Promise<{
    items: DCManagedItem[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const response = await api.get('/ordering/managed-items', { params })
    return response.data.data
  },

  async addManagedItems(medicineIds: number[], addedBy?: string): Promise<DCManagedItem[]> {
    const response = await api.post('/ordering/managed-items', { medicineIds, addedBy })
    return response.data.data
  },

  async addManualManagedItem(medicineId: number, addedBy?: string): Promise<DCManagedItem> {
    const response = await api.post('/ordering/managed-items/add-manual', { medicineId, addedBy })
    return response.data.data
  },

  async removeManagedItem(id: number): Promise<void> {
    await api.delete(`/ordering/managed-items/${id}`)
  },

  async bulkRemoveManagedItems(ids: number[]): Promise<{ removed: number }> {
    const response = await api.post('/ordering/managed-items/bulk-remove', { ids })
    return response.data.data
  },

  async refreshDemand(): Promise<DCDemandMap> {
    const response = await api.post('/ordering/managed-items/refresh-demand')
    return response.data.data
  },

  // ============================================
  // DC Ordering - Orders
  // ============================================

  async getOrders(params?: { page?: number; limit?: number; status?: string }): Promise<{
    orders: DCOrder[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const response = await api.get('/ordering/orders', { params })
    return response.data.data
  },

  async createOrder(params?: { managedItemIds?: number[]; notes?: string; createdBy?: string; qtyOverrides?: Record<number, number> }): Promise<DCOrderDetail> {
    const response = await api.post('/ordering/orders', params)
    return response.data.data
  },

  async getOrder(id: number): Promise<DCOrderDetail> {
    const response = await api.get(`/ordering/orders/${id}`)
    return response.data.data
  },

  async updateOrder(id: number, data: { status?: string; notes?: string }): Promise<void> {
    await api.put(`/ordering/orders/${id}`, data)
  },

  async deleteOrder(id: number): Promise<void> {
    await api.delete(`/ordering/orders/${id}`)
  },

  async updateOrderItem(orderId: number, itemId: number, data: { order_qty?: number; distributor_id?: number }): Promise<void> {
    await api.put(`/ordering/orders/${orderId}/items/${itemId}`, data)
  },

  async exportOrderCSV(orderId: number, distributorId?: number): Promise<Blob> {
    const response = await api.get(`/ordering/orders/${orderId}/export/csv`, {
      params: distributorId ? { distributorId } : undefined,
      responseType: 'blob'
    })
    return response.data
  },

  // ============================================
  // DC Purchases (Phase 2)
  // ============================================

  async createManualPurchase(data: {
    distributor_id?: number
    distributor_name?: string
    invoice_number?: string
    invoice_date?: string
    items: Array<{
      product_name: string
      pack_size?: string
      batch_no?: string
      expiry_date?: string
      quantity: number
      free_quantity?: number
      rate: number
      mrp?: number
      discount_pct?: number
      cgst_pct?: number
      sgst_pct?: number
      igst_pct?: number
      medicine_id?: number
      manufacturer_name?: string
    }>
  }): Promise<DCPurchaseUploadResponse> {
    const response = await api.post('/purchases/manual', data)
    return response.data.data
  },

  async uploadPurchaseBill(file: File, distributorId?: number, distributorName?: string): Promise<DCPurchaseUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    if (distributorId) formData.append('distributor_id', String(distributorId))
    if (distributorName) formData.append('distributor_name', distributorName)
    const response = await api.post('/purchases/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data.data
  },

  async getPurchases(params?: { page?: number; limit?: number; status?: string }): Promise<{
    purchases: DCPurchase[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const response = await api.get('/purchases', { params })
    return response.data.data
  },

  async getPurchase(id: number): Promise<DCPurchaseDetail> {
    const response = await api.get(`/purchases/${id}`)
    return response.data.data
  },

  async updatePurchase(id: number, data: { status?: string; notes?: string; distributor_id?: number; distributor_name?: string }): Promise<void> {
    await api.put(`/purchases/${id}`, data)
  },

  async deletePurchase(id: number): Promise<void> {
    await api.delete(`/purchases/${id}`)
  },

  async confirmPurchase(id: number): Promise<{ inventoryItemsCreated: number }> {
    const response = await api.post(`/purchases/${id}/confirm`)
    return response.data.data
  },

  async manualMatchPurchaseItem(purchaseId: number, itemId: number, medicineId: number): Promise<void> {
    await api.post(`/purchases/${purchaseId}/items/${itemId}/match`, { medicine_id: medicineId })
  },

  async updatePurchaseItem(purchaseId: number, itemId: number, data: Record<string, unknown>): Promise<DCPurchaseItem> {
    const response = await api.put(`/purchases/${purchaseId}/items/${itemId}`, data)
    return response.data.data
  },

  async getStoreSplit(purchaseId: number): Promise<DCStoreSplitReport> {
    const response = await api.get(`/purchases/${purchaseId}/store-split`)
    return response.data.data
  },

  async exportStoreSplitExcel(purchaseId: number): Promise<Blob> {
    const response = await api.get(`/purchases/${purchaseId}/store-split/export`, { responseType: 'blob' })
    return response.data
  },

  // ============================================
  // DC Inventory (Phase 2)
  // ============================================

  async getInventory(params?: {
    page?: number; limit?: number; search?: string; status?: string; distributorId?: number; expiryBefore?: string
  }): Promise<{
    items: DCInventoryItem[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const response = await api.get('/inventory', { params })
    return response.data.data
  },

  async getInventorySummary(): Promise<DCInventorySummary> {
    const response = await api.get('/inventory/summary')
    return response.data.data
  },

  async getInventoryItem(id: number): Promise<DCInventoryItem> {
    const response = await api.get(`/inventory/${id}`)
    return response.data.data
  },

  // ============================================
  // DC Store Transfers (Phase 2)
  // ============================================

  async createTransfer(params: {
    store_id: number; items: Array<{ dc_inventory_id: number; transfer_qty: number }>
    csv_format?: string; notes?: string
  }): Promise<DCTransferDetail> {
    const response = await api.post('/transfers', params)
    return response.data.data
  },

  async createTransfersBatch(params: {
    transfers: Array<{ store_id: number; items: Array<{ dc_inventory_id: number; transfer_qty: number }> }>
    csv_format?: string; notes?: string
  }): Promise<DCTransferDetail[]> {
    const response = await api.post('/transfers/batch', params)
    return response.data.data
  },

  async getTransfers(params?: { page?: number; limit?: number; status?: string; storeId?: number }): Promise<{
    transfers: DCStoreTransfer[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }> {
    const response = await api.get('/transfers', { params })
    return response.data.data
  },

  async getTransfer(id: number): Promise<DCTransferDetail> {
    const response = await api.get(`/transfers/${id}`)
    return response.data.data
  },

  async updateTransfer(id: number, data: { status?: string; notes?: string }): Promise<void> {
    await api.put(`/transfers/${id}`, data)
  },

  async deleteTransfer(id: number): Promise<void> {
    await api.delete(`/transfers/${id}`)
  },

  async confirmTransfer(id: number): Promise<void> {
    await api.post(`/transfers/${id}/confirm`)
  },

  async exportTransferCSV(id: number): Promise<Blob> {
    const response = await api.get(`/transfers/${id}/export/csv`, { responseType: 'blob' })
    return response.data
  },

  async exportTransferDistributorCSV(transferId: number, distributorId: number): Promise<Blob> {
    const response = await api.get(`/transfers/${transferId}/export/csv/${distributorId}`, { responseType: 'blob' })
    return response.data
  },

  async autoAllocate(inventoryIds: number[]): Promise<DCAutoAllocateResult[]> {
    const response = await api.post('/transfers/auto-allocate', { inventory_ids: inventoryIds })
    return response.data.data
  }
}
